import { useState, useEffect, useCallback, RefObject } from "react";

/**
 * Hook to detect if text content is truncated (text overflow with ellipsis)
 * @param ref - React ref to the element containing the text
 * @param dependencies - Optional array of dependencies that should trigger re-check
 * @returns boolean indicating if text is truncated
 */
export function useTextTruncation(ref: RefObject<HTMLElement>, dependencies: React.DependencyList = []): boolean {
  const [isTruncated, setIsTruncated] = useState(false);

  const checkTruncation = useCallback(() => {
    if (ref.current) {
      const { scrollWidth, clientWidth } = ref.current;
      setIsTruncated(scrollWidth > clientWidth);
    }
  }, [ref]);

  useEffect(() => {
    // Initial check
    checkTruncation();

    // Create ResizeObserver to watch for size changes
    const resizeObserver = new ResizeObserver(() => {
      checkTruncation();
    });

    if (ref.current) {
      resizeObserver.observe(ref.current);
    }

    // Also listen for window resize
    window.addEventListener("resize", checkTruncation);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", checkTruncation);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkTruncation, ref, ...dependencies]);

  return isTruncated;
}
